import { AuthenticatedHttp } from '@/http/axios';

/**
 * Todas as chamadas da NFS-e avulsa do painel (CKTMF-533).
 *
 * O back foi feito em paralelo contra um contrato escrito: se ele mudar, o ajuste cabe aqui, e
 * as telas não sabem o formato da API. A lista de notas é a exceção: ela é a rota de notas que já
 * existe (`getFiscalNotes`), com o parâmetro `origem`.
 */
const BASE = '/nfse-avulsa';

// O mesmo `code` que o motor devolve no 403 das rotas públicas: a conta ainda não está no piloto.
export const NFSE_FORA_DO_PILOTO = 'nfse_fora_do_piloto';

export const NOTA_AVULSA_STATUS = {
  processando: 'processando',
  autorizada: 'autorizada',
  erro: 'erro',
  cancelada: 'cancelada',
  substituida: 'substituida',
};

// O motor recusa fora desse intervalo; o front só antecipa a recusa.
export const JUSTIFICATIVA_MIN = 15;
export const JUSTIFICATIVA_MAX = 255;

const hex = (bytes) => Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

/**
 * Uma chave de idempotência nova (UUID v4). O `uuid` do projeto é ESM puro e não carrega no Jest
 * deste repo; o `crypto` do navegador resolve sem dependência.
 */
export const novaChaveDeIdempotencia = () => {
  const { crypto } = globalThis;
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();

  const bytes = new Uint8Array(16);
  if (typeof crypto?.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1)
      bytes[index] = Math.floor(Math.random() * 256);
  }
  // eslint-disable-next-line no-bitwise
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  // eslint-disable-next-line no-bitwise
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const value = hex(bytes);
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(
    16,
    20
  )}-${value.slice(20)}`;
};

/**
 * `onError` das queries da avulsa cujo erro já aparece na tela (aviso azul do piloto, "tentar de
 * novo", formulário sem o tomador anterior). Substitui o toast global de erro do app, que
 * mostraria o mesmo problema duas vezes, e em vermelho; o 403 do piloto nunca vira toast.
 */
export const erroNaTela = () => {};

export const isForaDoPiloto = (error) =>
  error?.response?.status === 403 && error?.response?.data?.code === NFSE_FORA_DO_PILOTO;

export const getSituacaoCadastro = async (options = {}) =>
  AuthenticatedHttp.get(`${BASE}/situacao/`, options).then((response) => response.data);

export const getRequisitos = async (options = {}) =>
  AuthenticatedHttp.get(`${BASE}/requisitos/`, options).then((response) => response.data);

export const getTomadoresAnteriores = async (busca = '', options = {}) =>
  AuthenticatedHttp.get(`${BASE}/tomadores/`, {
    params: busca ? { busca } : {},
    ...options,
  }).then((response) => response.data?.results ?? []);

export const getPreviaNota = async (payload) =>
  AuthenticatedHttp.post(`${BASE}/previa/`, payload).then((response) => response.data);

/**
 * A chave vem de quem chama, uma por prévia: repetir o pedido com ela devolve a mesma nota
 * (RN05), e é isso que segura o clique duplo e a nova tentativa depois de uma queda de rede.
 */
export const emitirNotaAvulsa = async (payload, idempotencyKey) =>
  AuthenticatedHttp.post(`${BASE}/notas/`, payload, {
    headers: { 'X-Idempotency-Key': idempotencyKey },
  }).then((response) => response.data);

export const getNotasAvulsas = async (params = {}, options = {}) =>
  AuthenticatedHttp.get(`${BASE}/notas/`, { params, ...options }).then((response) => response.data);

export const getNotaAvulsa = async (id, options = {}) =>
  AuthenticatedHttp.get(`${BASE}/notas/${id}/`, options).then((response) => response.data);

export const cancelarNotaAvulsa = async (id, justificativa) =>
  AuthenticatedHttp.post(`${BASE}/notas/${id}/cancelar/`, { justificativa }).then(
    (response) => response.data
  );

/**
 * Nome do tomador pela consulta de CNPJ que o cadastro fiscal já usa. Devolve `null` quando a
 * consulta não traz nome: o campo continua para a pessoa digitar.
 */
export const consultarNomeDoCnpj = async (cnpj, options = {}) =>
  AuthenticatedHttp.get('/financial/fiscal-data/company-info/', {
    params: { cnpj },
    ...options,
  }).then((response) => response.data?.fiscal_infos?.razao_social || null);

const firstMessage = (value) => {
  if (Array.isArray(value)) return firstMessage(value[0]);
  if (value && typeof value === 'object') return null;
  return value == null ? null : String(value);
};

/**
 * Os erros por campo do DRF, achatados no caminho com ponto (`tomador.documento`).
 *
 * `detail` e `non_field_errors` não são de campo e ficam de fora: quem chama mostra o `detail`.
 */
export const getErrosPorCampo = (error) => {
  const data = error?.response?.status === 400 ? error.response.data : null;
  const erros = {};

  const walk = (node, prefix) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return;

    Object.entries(node).forEach(([key, value]) => {
      if (!prefix && (key === 'detail' || key === 'non_field_errors' || key === 'code')) return;

      const path = prefix ? `${prefix}.${key}` : key;
      const message = firstMessage(value);

      if (message) {
        erros[path] = message;
      } else {
        walk(Array.isArray(value) ? value[0] : value, path);
      }
    });
  };

  walk(data, '');

  return erros;
};

export const getDetalheDoErro = (error) => {
  const data = error?.response?.data;

  return firstMessage(data?.detail) || firstMessage(data?.non_field_errors) || null;
};
